import { useId, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

interface FormFieldProps {
  label: string;
  /** Receives the ids to wire onto the control, so the label and messages stay associated. */
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

/**
 * Label, hint and error wrapper for controls that are not `<Input>` — selects, textareas, custom
 * widgets. `Input` carries its own label, so it does not need this.
 */
export function FormField({
  label,
  children,
  hint,
  error,
  required = false,
  className,
}: FormFieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className={cn('w-full', className)}>
      <label htmlFor={id} className="field-label">
        {label}
        {required ? (
          <span className="ms-0.5 text-red-600" aria-hidden>
            *
          </span>
        ) : null}
      </label>

      {children({ id, describedBy })}

      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-sm text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-muted mt-1.5 text-xs">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
